import { memo, useCallback } from 'react';

import { useToggleViewVisibilityMenuOption } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useToggleViewVisibilityMenuOption';
import { useToggleViewVisibilityMenuOptions } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useToggleViewVisibilityMenuOptions';
import { ViewVisibilityHeaderToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/shared/ViewVisibilityHeaderToggle';
import { ViewVisibilitySection } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilitySection/ViewVisibilitySection';
import { ViewVisibilityToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilitySection/ViewVisibilityToggle';
import { type ConfigViewOption } from '~/store/configViews/types';

export type ConfigViewVisbilityOptionsProps = {
  options: ConfigViewOption[];
};

export const ConfigViewVisbilityOptions = memo(({ options }: ConfigViewVisbilityOptionsProps) => {
  const { toggleViewVisibilityMenuOption } = useToggleViewVisibilityMenuOption();
  const { toggleViewVisibilityMenuOptions } = useToggleViewVisibilityMenuOptions();
  const initialShowOptions = options.some(({ isVisible }) => isVisible);

  // Only used by the single-option branch, but hooks must run before that early return.
  const handleToggleSingleOption = useCallback(() => {
    toggleViewVisibilityMenuOption(options[0]);
  }, [options, toggleViewVisibilityMenuOption]);

  const handleToggleShowingOptions = useCallback(
    (showing: boolean) => {
      toggleViewVisibilityMenuOptions(options, showing);
    },
    [options, toggleViewVisibilityMenuOptions],
  );

  if (options.length === 1) {
    const option = options[0];

    return (
      <ViewVisibilityHeaderToggle
        key={option.fieldName}
        id={option.fieldName}
        label={option.label}
        onToggle={handleToggleSingleOption}
        isToggleOn={option.isVisible}
      />
    );
  }

  return (
    <ViewVisibilitySection
      initialShowOptions={initialShowOptions}
      id="info"
      label="Metadata"
      onToggleShowingOptions={handleToggleShowingOptions}
      toggles={options.map((option) => (
        <ViewVisibilityToggle key={option.fieldName} onToggleOption={toggleViewVisibilityMenuOption} option={option} />
      ))}
    />
  );
});

ConfigViewVisbilityOptions.displayName = 'ConfigViewVisbilityOptions';
